import * as I from 'immutable';
import React from 'react';

import * as Remote from 'app/modules/Remote';
import {ApiOrderableScene} from 'app/modules/Remote/Feature';
import {StatusMaybe, useCachedApiGet} from 'app/utils/hookUtils';
import {BUNDLE_SOURCE_MAPPINGS} from 'app/utils/layers';

export interface OrderableScenesActions {
  getOrderableScenes: OrderableScenesLoader;
  invalidateOrderableScenes: InvalidateOrderableScenes;
}

export type OrderableScenesLoader = {
  (featureId: number): StatusMaybe<I.ImmutableOf<ApiOrderableScene[]>>;
  getProgress?: (featureId: number) => number | null;
};

export type InvalidateOrderableScenes = (featureId: number) => void;

export function useOrderableScenesLoader(
  featureCollectionId: number,
  projectId: string
): [OrderableScenesLoader, InvalidateOrderableScenes] {
  const progressMap = React.useRef(new Map<number, number>());

  const [getOrderableScenes, {invalidate}] = useCachedApiGet(
    async function* ([fcId, pId], fId: number) {
      const featuresApi = Remote.api.featureCollections.features(fcId as number);
      const pIdStr = pId as string;

      const sourceIdsResponse = await featuresApi.orderableSourceIds(pIdStr, fId);
      const sourceIds = sourceIdsResponse.get('data').toJS() as string[];
      const sourceGroups = groupSourceIdsByBundle(sourceIds);

      let allScenes = I.List<I.ImmutableOf<ApiOrderableScene>>();
      let completedGroups = 0;
      const totalGroups = sourceGroups.length;

      // Fire all group requests concurrently, then yield as each one settles
      // (in settlement order, not dispatch order) so the UI fills in progressively
      // and each yielded intermediate state is internally consistent: TC and MS
      // for a given source always arrive in the same group, so OrderTypeSwitcher
      // never sees a TC scene without its MS sibling mid-load.
      //
      // Tag each promise with its index so Promise.race can identify the winner,
      // then delete that index from the pool so it won't be picked again.
      const pending = new Map(
        sourceGroups.map((group, i) => [
          i,
          featuresApi.orderableScenes(pIdStr, fId, {}, group).then((r) => ({r, i})),
        ])
      );

      while (pending.size > 0) {
        const {r, i} = await Promise.race(pending.values());
        pending.delete(i);
        completedGroups++;
        allScenes = allScenes.concat(r.get('data')).toList();
        progressMap.current.set(fId, totalGroups > 0 ? completedGroups / totalGroups : 1);
        yield allScenes
          .sortBy((s) => s?.get('sensingTime'))
          .reverse()
          .toList();
      }

      progressMap.current.set(fId, 1);

      allScenes = allScenes
        .sortBy((s) => s?.get('sensingTime'))
        .reverse()
        .toList();

      return removeDuplicateScenes(allScenes);
    },
    [featureCollectionId, projectId]
  );

  return [
    Object.assign(getOrderableScenes, {
      getProgress: (featureId: number): number | null => progressMap.current.get(featureId) ?? null,
    }),
    invalidate,
  ];
}

/**
 * Adjusting this is a bit of a tradeoff. Lowering it tends
 * to get the first scenes in the order pane faster at the
 * expense of a longer loading period, while increasing it
 * increases the time to first scene but gets us fully loaded
 * faster and in fewer requests. A lower number means fewer
 * sources to match against in the BE request, speeding up the
 * query.
 */
const MAX_SOURCES_PER_GROUP = 3;

/**
 * Groups source IDs so that each bundle pair (truecolor + multispectral) is
 * always fetched together. This ensures that any yielded intermediate state is
 * internally consistent — consumers like OrderTypeSwitcher will never see a TC
 * scene without its MS sibling (or vice versa) mid-load.
 *
 * Non-bundle sources fall back to the original vendor-prefix grouping.
 */
function groupSourceIdsByBundle(sourceIds: string[]): string[][] {
  const remaining = new Set(sourceIds);
  const groups: string[][] = [];

  for (const mapping of Object.values(BUNDLE_SOURCE_MAPPINGS)) {
    const pair = [mapping.truecolor, mapping.multispectral].filter((id) => remaining.has(id));
    if (pair.length > 0) {
      groups.push(pair);
      pair.forEach((id) => remaining.delete(id));
    }
  }

  const byVendor = new Map<string, string[]>();
  for (const sourceId of remaining) {
    const vendor = sourceId.split('-')[0];
    const group = byVendor.get(vendor);
    if (group) {
      group.push(sourceId);
    } else {
      byVendor.set(vendor, [sourceId]);
    }
  }

  for (const vendorGroup of byVendor.values()) {
    for (let i = 0; i < vendorGroup.length; i += MAX_SOURCES_PER_GROUP) {
      groups.push(vendorGroup.slice(i, i + MAX_SOURCES_PER_GROUP));
    }
  }

  return groups;
}

// We can't guarantee that the response from the orderableScenes api does not include
// any duplicates. It is unlikely, but just in case, we check and filter them out.
function removeDuplicateScenes(scenes: I.ImmutableOf<ApiOrderableScene[]>) {
  const seen = new Set<string>();
  return scenes
    .filter((scene) => {
      if (!scene) return false;
      const key = `${scene.get('sensingTime')}::${scene.get('sourceId')}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .toList();
}
